import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Container, Form, ListGroup, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaFileCsv, FaFileExcel, FaSearch } from "react-icons/fa";
import { searchUsers } from "@/services/resourcesApi";
import { apiClient } from "@/services/apiClient";

type UserSearchResult = {
  id?: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  email?: string;
  upn?: string;
};

type UnifiedGroup = {
  id: string;
  displayName?: string;
  description?: string;
  mail?: string;
  visibility?: string;
  roles: Array<"member" | "owner">;
};

type ExportRow = {
  name: string;
  description: string;
  mail: string;
  visibility: string;
  role: string;
  groupId: string;
};

const AdminCheckSitePermissions: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInput, setUserInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{ displayName: string; email: string }>>([]);
  const [groups, setGroups] = useState<UnifiedGroup[]>([]);

  useEffect(() => {
    const term = userInput.trim();
    if (!showSuggestions || term.length < 2) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSuggestionsLoading(true);
        const found = await searchUsers(term);
        if (cancelled) return;

        const mapped = (found as UserSearchResult[])
          .map((u, idx) => {
            const email = (u.mail || u.userPrincipalName || u.email || u.upn || "").trim();
            const displayName = (u.displayName || email || `User ${idx + 1}`).trim();
            if (!email) return null;
            return { displayName, email };
          })
          .filter((v): v is { displayName: string; email: string } => Boolean(v))
          .slice(0, 8);

        setSuggestions(mapped);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [userInput, showSuggestions]);

  const groupStats = useMemo(() => {
    const owners = groups.filter((g) => g.roles.includes("owner")).length;
    const members = groups.filter((g) => g.roles.includes("member")).length;
    return { owners, members };
  }, [groups]);

  const buildExportRows = (): ExportRow[] =>
    groups.map((g) => ({
      name: g.displayName || g.id,
      description: g.description || "",
      mail: g.mail || "",
      visibility: g.visibility || "",
      role: [g.roles.includes("owner") ? "Owner" : "", g.roles.includes("member") ? "Member" : ""]
        .filter(Boolean)
        .join(", "),
      groupId: g.id,
    }));

  const safeFilePart = (value: string) => value.replace(/[^a-z0-9._-]/gi, "_");

  const getDateTimeSuffix = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${yyyy}${mm}${dd}_${hh}${min}${ss}`;
  };

  const downloadFile = (content: string, fileName: string, mimeType: string) => {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const csvEscape = (value: string) => `"${(value || "").replace(/"/g, '""')}"`;

  const exportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;

    const header = ["Name", "Description", "Mail", "Visibility", "Role", "GroupId"];
    const lines = [
      header.join(","),
      ...rows.map((r) => [r.name, r.description, r.mail, r.visibility, r.role, r.groupId].map(csvEscape).join(",")),
    ];

    const fileName = `check-site-permissions_${safeFilePart(userInput.trim() || "results")}_${getDateTimeSuffix()}.csv`;
    downloadFile(lines.join("\n"), fileName, "text/csv");
  };

  const exportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;

    const tableRows = rows
      .map(
        (r) =>
          `<tr><td>${r.name}</td><td>${r.description}</td><td>${r.mail}</td><td>${r.visibility}</td><td>${r.role}</td><td>${r.groupId}</td></tr>`
      )
      .join("");

    const html = [
      "<html><head><meta charset=\"utf-8\"></head><body>",
      "<table border=\"1\">",
      "<thead><tr><th>Name</th><th>Description</th><th>Mail</th><th>Visibility</th><th>Role</th><th>GroupId</th></tr></thead>",
      `<tbody>${tableRows}</tbody>`,
      "</table>",
      "</body></html>",
    ].join("");

    const fileName = `check-site-permissions_${safeFilePart(userInput.trim() || "results")}_${getDateTimeSuffix()}.xls`;
    downloadFile(html, fileName, "application/vnd.ms-excel");
  };

  const runLookup = async () => {
    const upn = userInput.trim();
    if (!upn) {
      setError("Enter a user email address to search.");
      return;
    }

    setLoading(true);
    setError(null);
    setGroups([]);

    try {
      const response = await apiClient.get<UnifiedGroup[]>("/api/admin/sharepoint/check-site-permissions/groups", {
        params: { upn },
      });

      const combined = [...(response.data || [])].sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
      setGroups(combined);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to lookup user groups.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h2 className="mb-1">Check Site Permissions</h2>
          <p className="text-muted mb-0">Lookup unified groups where a user is a member and/or owner.</p>
        </div>
        <Link to="/admin/groups" className="btn btn-outline-secondary btn-sm">
          <FaArrowLeft className="me-2" />
          Back to Groups Admin
        </Link>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <div className="row g-3 align-items-end">
            <div className="col-md-8 position-relative">
              <Form.Group>
                <Form.Label className="small fw-semibold">User Email (typeahead)</Form.Label>
                <Form.Control
                  value={userInput}
                  onChange={(e) => {
                    setUserInput(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void runLookup();
                    }
                  }}
                  autoComplete="off"
                  placeholder="name@enmax.com"
                />
              </Form.Group>

              {showSuggestions && (
                <div className="position-absolute bg-white border rounded shadow-sm w-100 mt-1" style={{ zIndex: 1000, maxHeight: 260, overflowY: "auto" }}>
                  {suggestionsLoading && (
                    <div className="p-2 small text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Searching...
                    </div>
                  )}

                  {!suggestionsLoading && suggestions.length > 0 && (
                    <ListGroup variant="flush">
                      {suggestions.map((s, idx) => (
                        <ListGroup.Item
                          key={`${s.email}-${idx}`}
                          action
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setUserInput(s.email);
                            setShowSuggestions(false);
                          }}
                        >
                          <div className="fw-semibold">{s.displayName}</div>
                          <div className="small text-muted">{s.email}</div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              )}
            </div>
            <div className="col-md-4 d-grid">
              <Button variant="primary" onClick={() => void runLookup()} disabled={loading || !userInput.trim()}>
                <FaSearch className="me-2" />
                {loading ? "Searching..." : "Search Groups"}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {groups.length > 0 && (
        <Alert variant="info" className="py-2">
          <span className="small">
            Total groups: <strong>{groups.length}</strong> | Owner in: <strong>{groupStats.owners}</strong> | Member in: <strong>{groupStats.members}</strong>
          </span>
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-end gap-2 mb-3">
            <Button variant="outline-secondary" size="sm" onClick={exportCsv} disabled={!groups.length}>
              <FaFileCsv className="me-2" />
              Export CSV
            </Button>
            <Button variant="outline-success" size="sm" onClick={exportExcel} disabled={!groups.length}>
              <FaFileExcel className="me-2" />
              Export Excel
            </Button>
          </div>

          {loading ? (
            <div className="text-muted"><Spinner animation="border" size="sm" className="me-2" />Searching groups...</div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Mail</th>
                    <th>Visibility</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <a href={`/M365/Group?g=${encodeURIComponent(g.id)}`} target="_blank" rel="noreferrer">
                          {g.displayName || g.id}
                        </a>
                      </td>
                      <td className="small">{g.description || "-"}</td>
                      <td className="small">{g.mail || "-"}</td>
                      <td className="small">{g.visibility || "-"}</td>
                      <td>
                        {g.roles.includes("owner") && <span className="badge bg-warning text-dark me-1">Owner</span>}
                        {g.roles.includes("member") && <span className="badge bg-info text-dark">Member</span>}
                      </td>
                    </tr>
                  ))}

                  {groups.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="text-muted text-center py-3">Enter a user email and click Search Groups.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminCheckSitePermissions;
